import type { Contract } from '../types';
import * as contractApi from '../api/contract';
import { KeyedAsyncCache } from './asyncCache';

const BUSINESS_TYPE_FROM_BACKEND: Record<contractApi.BackendContractBusinessType, NonNullable<Contract['loaiHopDong']>> = {
  HTX_FARMER: 'HTX-Nông hộ',
  HTX_BUYER: 'HTX-Đối tác thu mua',
  SUPPLY: 'Cung ứng/Vật tư',
};

/** Backend has no equivalent of the mock's "Thiếu tài liệu"/"Tạm dừng" states,
 * and its own SENT/OTP_PENDING e-signing steps collapse into "Chờ ký" — the
 * frontend never needs to distinguish those two sub-steps. "Sắp hết hạn" is
 * derived below (from expiryDate), not a stored backend status. */
const STATUS_FROM_BACKEND: Record<contractApi.BackendContractStatus, Contract['status']> = {
  DRAFT: 'Nháp',
  SENT: 'Chờ ký',
  OTP_PENDING: 'Chờ ký',
  SIGNED: 'Đang hiệu lực',
  ACTIVE: 'Đang hiệu lực',
  AMENDED: 'Đang hiệu lực',
  EXPIRED: 'Hết hạn',
  TERMINATED: 'Hủy',
};

const EXPIRING_SOON_DAYS = 30;

function toFrontendContract(c: contractApi.BackendContract): Contract {
  const status = STATUS_FROM_BACKEND[c.status] ?? 'Nháp';
  // A separate flag, never folded into `status` — see the field's own doc
  // comment in types/index.ts (Feedback 08 A2: this used to overwrite status
  // itself, hiding "Đang hiệu lực" from every consumer that checked for it).
  let expiringSoon = false;
  if (status === 'Đang hiệu lực' && c.expiryDate) {
    const daysLeft = (new Date(c.expiryDate).getTime() - Date.now()) / 86_400_000;
    if (daysLeft >= 0 && daysLeft <= EXPIRING_SOON_DAYS) expiringSoon = true;
  }
  return {
    id: c._id,
    farmerId: c.farmerId,
    soHopDong: c.contractNo || `#${c._id.slice(-6)}`,
    loaiHopDong: BUSINESS_TYPE_FROM_BACKEND[c.businessType],
    doiTac: c.counterpartyName,
    ngayKy: c.signedDate || c.createdAt.slice(0, 10),
    ngayHieuLuc: c.effectiveDate,
    ngayHetHan: c.expiryDate || '',
    noiDung: c.notes || `Hợp đồng ${c.mainCrop ? c.mainCrop + ' — ' : ''}${c.templateId}`,
    giaTri: Number(c.valueVnd ?? '0'),
    donGiaDuKien: c.expectedUnitPriceVnd,
    status,
    expiringSoon,
    seasonIds: c.seasonIds,
    cayTrong: c.mainCrop,
    giong: c.variety,
    dienTich: c.areaHa,
    sanLuongDuKien: c.expectedOutputKg,
    ngayGiaoHangDuKien: c.expectedDeliveryDate,
    cropLines: c.cropLines ?? [],
    attachments: c.attachments,
    ghiChu: c.notes,
    isDeleted: false,
    hanMucDaCap: Number(c.limitGrantedVnd ?? '0'),
    hanMucConLai: c.limitRemainingVnd !== undefined ? Number(c.limitRemainingVnd) : undefined,
  };
}

const byFarmer = new KeyedAsyncCache<Contract[]>(
  async (farmerId) => (await contractApi.listContractsByFarmer(farmerId)).map(toFrontendContract),
  [],
  'contracts:byFarmer',
);
const byHtx = new KeyedAsyncCache<Contract[]>(
  async (htxId) => (await contractApi.listContractsByHtx(htxId)).items.map(toFrontendContract),
  [],
  'contracts:byHtx',
);

export function getContractsByFarmer(farmerId?: string): Contract[] {
  if (!farmerId) return [];
  return byFarmer.get(farmerId);
}

/** Call after a mutation this store didn't itself perform — e.g. the farmer
 * verifying their own contract-signing OTP directly against the contract API
 * (not routed through a store function here), which otherwise leaves the
 * cached list showing the pre-signing status until something else happens to
 * invalidate it. */
export function invalidateContractsForFarmer(farmerId: string): void {
  byFarmer.invalidate(farmerId);
  byFarmer.get(farmerId);
}

/** HTX_STAFF, unlike ADMIN/OPS/CO, cannot create/send/activate/amend/terminate
 * a Contract on the real backend (confirmed against every route's @Roles) —
 * this domain is read-only from the HTX web app for now. Creation is a
 * credit-officer/back-office action, not something to fake through a role
 * the backend doesn't grant. */
export function getContractsByHtx(htxId?: string): Contract[] {
  if (!htxId) return [];
  return byHtx.get(htxId);
}

export interface CreateContractInput {
  htxId: string;
  farmerId: string;
  /** Absent for a profile HTX pre-created without an account: the contract is
   * then drafted with the HTX as the signing party, and the farmer is added as
   * a party once their account is linked (EkycService.linkFarmerToExistingProfile). */
  farmerUserId?: string;
  /** The HTX staff member creating the contract — the signing party when the
   * farmer has no account yet. */
  htxUserId: string;
  templateId: string;
  businessType: contractApi.BackendContractBusinessType;
  counterpartyName?: string;
  effectiveDate?: string;
  expiryDate?: string;
  valueVnd?: number;
  expectedUnitPriceVnd?: number;
  mainCrop?: string;
  variety?: string;
  areaHa?: number;
  expectedOutputKg?: number;
  expectedDeliveryDate?: string;
  cropLines?: contractApi.BackendContractCropLine[];
  seasonIds?: string[];
  notes?: string;
  attachments?: contractApi.BackendContractAttachment[];
}

export async function createContract(input: CreateContractInput): Promise<Contract> {
  const created = await contractApi.createContractDraft({
    htxId: input.htxId,
    farmerId: input.farmerId,
    templateId: input.templateId,
    terms: { notes: input.notes || '' },
    parties: input.farmerUserId ? [{ role: 'FARMER', userId: input.farmerUserId }] : [{ role: 'HTX', userId: input.htxUserId }],
    businessType: input.businessType,
    counterpartyName: input.counterpartyName,
    effectiveDate: input.effectiveDate,
    expiryDate: input.expiryDate,
    valueVnd: input.valueVnd,
    expectedUnitPriceVnd: input.expectedUnitPriceVnd,
    mainCrop: input.mainCrop,
    variety: input.variety,
    areaHa: input.areaHa,
    expectedOutputKg: input.expectedOutputKg,
    expectedDeliveryDate: input.expectedDeliveryDate,
    cropLines: input.cropLines,
    seasonIds: input.seasonIds,
    notes: input.notes,
    attachments: input.attachments,
  });
  byHtx.invalidate(input.htxId);
  byHtx.get(input.htxId);
  byFarmer.invalidate(input.farmerId);
  byFarmer.get(input.farmerId);
  return toFrontendContract(created);
}

export interface UpdateContractInput {
  counterpartyName?: string;
  expiryDate?: string;
  valueVnd?: number;
  mainCrop?: string;
  variety?: string;
  areaHa?: number;
  expectedOutputKg?: number;
  expectedUnitPriceVnd?: number;
  expectedDeliveryDate?: string;
  notes?: string;
}

export async function updateContract(
  contractId: string,
  input: UpdateContractInput,
  farmerId?: string,
  htxId?: string,
): Promise<Contract> {
  const updated = await contractApi.updateContractDraft(contractId, input);
  const contract = toFrontendContract(updated);
  if (farmerId) {
    byFarmer.invalidate(farmerId);
    byFarmer.get(farmerId);
  }
  if (htxId) {
    byHtx.invalidate(htxId);
    byHtx.get(htxId);
  }
  return contract;
}

export async function addContractAttachment(
  contractId: string,
  attachment: contractApi.BackendContractAttachment,
  farmerId?: string,
  htxId?: string,
): Promise<Contract> {
  const contract = toFrontendContract(await contractApi.addContractAttachment(contractId, attachment));
  if (farmerId) byFarmer.invalidate(farmerId);
  if (htxId) byHtx.invalidate(htxId);
  return contract;
}

export async function linkContractSeason(contractId: string, seasonId: string, farmerId?: string, htxId?: string): Promise<Contract> {
  const updated = await contractApi.linkContractSeason(contractId, seasonId);
  const contract = toFrontendContract(updated);
  if (farmerId) {
    byFarmer.invalidate(farmerId);
    byFarmer.get(farmerId);
  }
  if (htxId) {
    byHtx.invalidate(htxId);
    byHtx.get(htxId);
  }
  return contract;
}
